import api from './api';

export interface SellerVerification {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
  };
  storeId?: {
    _id: string;
    name: string;
  };
  nationalId?: string;
  businessLicense?: string;
  documents: {
    type: string;
    url: string;
    uploadedAt: string;
  }[];
  status: 'pending' | 'approved' | 'rejected' | 'needs_revision';
  rejectionReason?: string;
  reviewedBy?: {
    _id: string;
    name: string;
  };
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VerificationsResponse {
  items: SellerVerification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class SellerVerificationService {
  private baseURL = '/admin/sellers';

  /**
   * Get all seller verifications
   */
  async getVerifications(
    page: number = 1,
    limit: number = 20,
    filters?: {
      status?: string;
      userId?: string;
    }
  ): Promise<VerificationsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (filters?.status) params.append('status', filters.status);
    if (filters?.userId) params.append('userId', filters.userId);

    const response = await api.get(`${this.baseURL}/verifications?${params.toString()}`);
    const data = (response as any)?.data || response;
    return {
      items: data?.items || data?.docs || [],
      total: data?.total || data?.totalDocs || 0,
      page: data?.page || page,
      limit: data?.limit || limit,
      totalPages: data?.totalPages || data?.pages || 1
    };
  }

  /**
   * Get verification details
   */
  async getVerificationDetails(id: string): Promise<SellerVerification> {
    const response = await api.get(`${this.baseURL}/verifications/${id}`);
    return (response as any)?.data || response;
  }

  /**
   * Review verification (approve/reject)
   */
  async reviewVerification(
    id: string,
    action: 'approve' | 'reject' | 'needs_revision',
    reason?: string
  ): Promise<SellerVerification> {
    const response = await api.patch(`${this.baseURL}/verifications/${id}/review`, {
      action,
      reason
    });
    return (response as any)?.data || response;
  }

  /**
   * Get status color
   */
  getStatusColor(status: string): 'warning' | 'success' | 'error' | 'info' | 'default' {
    const colorMap: Record<string, 'warning' | 'success' | 'error' | 'info' | 'default'> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      needs_revision: 'info'
    };
    return colorMap[status] || 'default';
  }

  /**
   * Get status label
   */
  getStatusLabel(status: string): string {
    const labelMap: Record<string, string> = {
      pending: 'در انتظار بررسی',
      approved: 'تایید شده',
      rejected: 'رد شده',
      needs_revision: 'نیاز به اصلاح'
    };
    return labelMap[status] || status;
  }
}

export const sellerVerificationService = new SellerVerificationService();
export default sellerVerificationService;

